// ESTADISTICAS — decide cuál de las dos pantallas se muestra.
//
// Hay dos pantallas de estadísticas conviviendo, y esta capa elige. El menú
// tiene UNA sola entrada, "Estadísticas": el cliente no ve que existen dos.
//
// La regla, en orden de prioridad:
//
//   1. `?stats=new` / `?stats=old` en la URL — para soporte. Es la única forma
//      de mostrarle la pantalla nueva a un cliente que todavía no actualizó, y
//      la salida de emergencia si la nueva falla en producción (no hace falta
//      redeployar para volver atrás).
//   2. Lo que el cliente eligió — ver abajo.
//   3. La versión de su TicketControl.
//
// POR QUÉ POR VERSIÓN Y NO PARA TODOS A LA VEZ. No es sólo repartir la carga de
// soporte: es que atado a una actualización el cambio trae una explicación que
// el cliente ya acepta ("actualizaste y la pantalla mejoró"). Entrar un martes
// y encontrar todo distinto sin haber tocado nada genera desconfianza, no
// entusiasmo. Además las pantallas viejas no publican todo lo que la nueva
// necesita —la curva de ocupación llegó en la 2.0.48— así que a un cliente
// atrasado la nueva le luciría a medias.
//
// POR QUÉ EL CLIENTE PUEDE VOLVER ATRÁS. Lo que apaga la pregunta "¿no se puede
// ver como antes?" no es que el cambio sea gradual: es que la respuesta sea que
// sí. Las dos pantallas existen igual, así que darle la opción cuesta nada. Y
// deja un dato que no se consigue preguntando: si al mes nadie volvió, la vieja
// se puede retirar tranquila; si vuelve la mitad, algo de la nueva no funciona.
//
// La preferencia sólo puede ir HACIA ATRÁS. Un cliente que vio la nueva puede
// quedarse en la vieja, pero no al revés: adelantarse a su versión le mostraría
// una pantalla incompleta. Para eso está el override de la URL.

import React, { useState, useCallback } from "react";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";

import Stats from "../Stats";
import Stats2 from "../Stats2";

// Desde esta versión del Desktop, la pantalla nueva tiene todo lo que necesita.
export const VERSION_MINIMA = "2.0.70";

const CLAVE = "tc_estadisticas_preferencia";

/** Compara "2.0.70" con "2.0.9" sin que gane el orden alfabético. */
export const versionAlMenos = (version, minima) => {
  const a = String(version || "0.0.0").split(".").map((n) => parseInt(n, 10) || 0);
  const b = String(minima).split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] || 0;
    const y = b[i] || 0;
    if (x !== y) return x > y;
  }
  return true;
};

// La preferencia es de la PERSONA, no del estacionamiento: quien administra
// tres playas no quiere elegir tres veces. Y todo va envuelto porque en ventana
// privada `localStorage` puede tirar al leerlo, no sólo devolver vacío.
const leerPreferencia = () => {
  try {
    return window.localStorage.getItem(CLAVE);
  } catch {
    return null;
  }
};

const guardarPreferencia = (valor) => {
  try {
    if (valor === null) window.localStorage.removeItem(CLAVE);
    else window.localStorage.setItem(CLAVE, valor);
  } catch {
    /* sin persistencia: vale para esta sesión y ya */
  }
};

const Aviso = ({ children, onClick, texto }) => (
  <div className="text-center text-muted pb-4" style={{ fontSize: 12.5 }}>
    {children}{" "}
    <button
      type="button"
      onClick={onClick}
      className="btn btn-link p-0 align-baseline"
      style={{ fontSize: 12.5 }}
    >
      {texto}
    </button>
  </div>
);

const Estadisticas = ({ versionApp }) => {
  const [params] = useSearchParams();
  const [preferencia, setPreferencia] = useState(leerPreferencia);

  const forzado = params.get("stats"); // "new" | "old" | null
  const puedeVerNueva = versionAlMenos(versionApp, VERSION_MINIMA);

  let mostrarNueva;
  if (forzado === "new") mostrarNueva = true;
  else if (forzado === "old") mostrarNueva = false;
  else if (preferencia === "old") mostrarNueva = false;
  else mostrarNueva = puedeVerNueva;

  const elegir = useCallback((valor) => {
    guardarPreferencia(valor);
    setPreferencia(valor);
    // Arriba de todo: las dos pantallas son largas y el enlace vive al pie.
    window.scrollTo({ top: 0 });
  }, []);

  // El pie viaja como prop y lo pinta la pantalla DENTRO de su Container.
  // Dibujarlo acá, como hermano, lo dejaba tapado por el footer fijo:
  // `.page-content` reserva abajo ese alto (assets/scss/.../_topbar.scss).
  //
  // Con el override puesto no se ofrece nada: quien lo usa es soporte, y un
  // clic ahí le guardaría una preferencia al cliente sin querer.
  let pie = null;
  if (!forzado && mostrarNueva) {
    pie = (
      <Aviso onClick={() => elegir("old")} texto="Ver la versión anterior">
        Esta es la vista nueva de Estadísticas.
      </Aviso>
    );
  } else if (!forzado && !mostrarNueva && puedeVerNueva && preferencia === "old") {
    // Sólo si eligió volver Y su versión le permite la nueva. A un cliente con
    // TicketControl viejo no se le ofrece algo que no puede tener.
    pie = (
      <Aviso onClick={() => elegir(null)} texto="Volver a la vista nueva">
        Estás viendo la versión anterior de Estadísticas.
      </Aviso>
    );
  }

  return mostrarNueva ? <Stats2 pie={pie} /> : <Stats pie={pie} />;
};

const mapStateToProps = (state) => ({
  // La publica el Desktop en app_config; ListenerDB ya la deja en el store.
  versionApp: state.data.versionDB,
});

export default connect(mapStateToProps)(Estadisticas);
